import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, LifeBuoy, Plus, Send, X, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime, fmtTime } from "@/utils/datetime";

type Ticket = { id: string; subject: string; category: string; priority: string; status: string; created_at: string; last_message_at?: string; message_count: number; opened_by_user_id?: string };
type Message = { id: string; body: string; author_user_id?: string; author_is_platform: boolean; is_internal: boolean; created_at: string };

const CATEGORIES = ["technical", "financial", "question", "suggestion", "other"] as const;
const PRIORITIES = ["low", "normal", "high", "urgent"] as const;
const STATUSES = ["open", "in_progress", "resolved", "closed"] as const;

export default function Support() {
    const { t, i18n } = useTranslation();
    const { me } = useAuth();
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [filter, setFilter] = useState<string>("");
    const [selected, setSelected] = useState<string | null>(null);
    const [detail, setDetail] = useState<{ ticket: Ticket; messages: Message[] } | null>(null);
    const [openNew, setOpenNew] = useState(false);
    const [reply, setReply] = useState("");
    const [busy, setBusy] = useState(false);

    const load = () => {
        const params: any = {};
        if (filter) params.status = filter;
        api.get("/support/tickets", { params }).then(r => setTickets(r.data.items)).catch(e => toast.error(formatApiError(e)));
    };
    useEffect(() => { load(); }, [filter]);
    useEffect(() => {
        if (!selected) { setDetail(null); return; }
        api.get(`/support/tickets/${selected}`).then(r => setDetail(r.data)).catch(e => toast.error(formatApiError(e)));
    }, [selected]);

    const sendReply = async () => {
        if (!selected || !reply.trim()) return;
        setBusy(true);
        try {
            await api.post(`/support/tickets/${selected}/messages`, { body: reply.trim() });
            setReply("");
            const r = await api.get(`/support/tickets/${selected}`);
            setDetail(r.data);
            load();
        } catch (e: any) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };
    const closeTicket = async () => {
        if (!selected) return;
        try {
            await api.post(`/support/tickets/${selected}/close`);
            toast.success(t("support.closed"));
            load();
            const r = await api.get(`/support/tickets/${selected}`);
            setDetail(r.data);
        } catch (e: any) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="space-y-4" data-testid="support-page">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <LifeBuoy size={20} style={{ color: "var(--primary)" }} />
                    <h1 className="text-2xl font-semibold">{t("support.title")}</h1>
                </div>
                <button data-testid="support-new" onClick={() => setOpenNew(true)}
                    className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium"
                    style={{ background: "var(--primary)", color: "var(--primary-fg)" }}>
                    <Plus size={14} />{t("support.new")}
                </button>
            </div>
            <div className="flex gap-1 flex-wrap">
                {["", ...STATUSES].map(s => (
                    <button key={s || "all"} data-testid={`support-filter-${s || "all"}`}
                        onClick={() => setFilter(s)}
                        className="rounded-md px-2.5 py-1 text-xs"
                        style={{ background: filter === s ? "var(--selected-bg)" : "var(--input)", color: "var(--text)" }}>
                        {t(s ? `support.status.${s}` : "common.all")}
                    </button>
                ))}
            </div>
            <div className="grid md:grid-cols-3 gap-4">
                <div className="md:col-span-1 rounded-xl border overflow-hidden" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
                    <div className="px-3 py-2 text-xs font-medium" style={{ background: "var(--input)" }}>{t("support.my_tickets")}</div>
                    <div className="max-h-[560px] overflow-y-auto">
                        {tickets.map(tk => (
                            <button key={tk.id} data-testid={`support-ticket-${tk.id}`}
                                onClick={() => setSelected(tk.id)}
                                className="w-full text-left border-b px-3 py-2 hover:bg-[var(--selected-bg)]"
                                style={{ borderColor: "var(--border)", background: selected === tk.id ? "var(--selected-bg)" : "transparent" }}>
                                <div className="flex items-center justify-between gap-2">
                                    <div className="font-medium text-sm truncate">{tk.subject}</div>
                                    <StatusPill s={tk.status} t={t} />
                                </div>
                                <div className="text-xs mt-1 flex gap-2" style={{ color: "var(--text-muted)" }}>
                                    <span>{t(`support.category.${tk.category}`)}</span>
                                    <span>·</span>
                                    <span>{t(`support.priority.${tk.priority}`)}</span>
                                </div>
                            </button>
                        ))}
                        {tickets.length === 0 && <div className="px-3 py-6 text-center text-sm" style={{ color: "var(--text-muted)" }}>{t("support.empty")}</div>}
                    </div>
                </div>
                <div className="md:col-span-2 rounded-xl border overflow-hidden flex flex-col" style={{ borderColor: "var(--border)", background: "var(--card)", minHeight: 400 }} data-testid="support-detail">
                    {!detail && <div className="flex-1 flex items-center justify-center text-sm" style={{ color: "var(--text-muted)" }}>{t("support.pick")}</div>}
                    {detail && (
                        <>
                            <div className="px-4 py-3 border-b flex items-start justify-between gap-3" style={{ borderColor: "var(--border)" }}>
                                <div>
                                    <div className="text-lg font-semibold">{detail.ticket.subject}</div>
                                    <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                                        {t(`support.category.${detail.ticket.category}`)} · {t(`support.priority.${detail.ticket.priority}`)} · {fmtDateTime(detail.ticket.created_at)}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <StatusPill s={detail.ticket.status} t={t} />
                                    {detail.ticket.status !== "closed" && detail.ticket.opened_by_user_id === me?.user.id && (
                                        <button data-testid="support-close" onClick={closeTicket} className="text-xs" style={{ color: "var(--text-muted)" }}>{t("support.close")}</button>
                                    )}
                                </div>
                            </div>
                            <div className="flex-1 overflow-y-auto p-4 space-y-3">
                                {detail.messages.map(m => (
                                    <div key={m.id} className={`flex ${m.author_is_platform ? "justify-start" : "justify-end"}`}>
                                        <div className="max-w-[75%] rounded-lg p-3 text-sm" style={{
                                            background: m.author_is_platform ? "var(--info-bg)" : "var(--selected-bg)",
                                            color: m.author_is_platform ? "var(--info-fg)" : "var(--text)",
                                        }} data-testid={`support-msg-${m.id}`}>
                                            <div className="text-[10px] uppercase tracking-wider opacity-70 mb-1">
                                                {m.author_is_platform ? t("support.by_platform") : t("support.by_me")}
                                                <span className="ml-2 opacity-70">{fmtTime(m.created_at)}</span>
                                            </div>
                                            <div className="whitespace-pre-wrap">{m.body}</div>
                                        </div>
                                    </div>
                                ))}
                                {detail.messages.length === 0 && <div className="text-center text-sm" style={{ color: "var(--text-muted)" }}>{t("support.no_messages")}</div>}
                            </div>
                            {detail.ticket.status !== "closed" && (
                                <div className="p-3 border-t flex gap-2" style={{ borderColor: "var(--border)" }}>
                                    <textarea data-testid="support-reply" value={reply} onChange={e => setReply(e.target.value)} rows={2}
                                        placeholder={t("support.reply_placeholder")}
                                        className="surface-input flex-1 px-3 py-2 text-sm resize-none" />
                                    <button data-testid="support-send" disabled={busy || !reply.trim()} onClick={sendReply}
                                        className="inline-flex items-center gap-1.5 rounded-md px-4 text-sm font-medium disabled:opacity-60"
                                        style={{ background: "var(--primary)", color: "var(--primary-fg)" }}>
                                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
            {openNew && <NewTicket onClose={() => setOpenNew(false)} onCreated={() => { load(); setOpenNew(false); }} />}
        </div>
    );
}

function StatusPill({ s, t }: { s: string; t: any }) {
    const map: Record<string, { bg: string; fg: string }> = {
        open: { bg: "var(--info-bg)", fg: "var(--info-fg)" },
        in_progress: { bg: "var(--warning-bg)", fg: "var(--warning-fg)" },
        resolved: { bg: "var(--success-bg)", fg: "var(--success-fg)" },
        closed: { bg: "var(--input)", fg: "var(--text-muted)" },
    };
    const c = map[s] || { bg: "var(--input)", fg: "var(--text)" };
    return <span className="text-[11px] rounded-full px-2 py-0.5" style={{ background: c.bg, color: c.fg }} data-testid={`support-status-${s}`}>{t(`support.status.${s}`)}</span>;
}

function NewTicket({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
    const { t } = useTranslation();
    const [subject, setSubject] = useState("");
    const [category, setCategory] = useState<string>("question");
    const [priority, setPriority] = useState<string>("normal");
    const [body, setBody] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (subject.trim().length < 2 || body.trim().length < 2) return;
        setBusy(true);
        try {
            await api.post("/support/tickets", { subject: subject.trim(), category, priority, body: body.trim() });
            toast.success(t("support.created"));
            onCreated();
        } catch (e: any) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.6)" }}>
            <div className="w-full max-w-md rounded-xl border p-5 space-y-3" style={{ background: "var(--card)", borderColor: "var(--border)" }} data-testid="support-new-dialog">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold">{t("support.new")}</h2>
                    <button onClick={onClose}><X size={16} /></button>
                </div>
                <label className="block text-sm">{t("support.subject")}
                    <input data-testid="support-new-subject" value={subject} onChange={e => setSubject(e.target.value)} minLength={2} maxLength={255}
                        className="surface-input mt-1 w-full px-3 py-2 text-sm" />
                </label>
                <div className="grid grid-cols-2 gap-2">
                    <label className="block text-sm">{t("support.category_label")}
                        <select data-testid="support-new-category" value={category} onChange={e => setCategory(e.target.value)}
                            className="surface-input mt-1 w-full px-3 py-2 text-sm">
                            {CATEGORIES.map(c => <option key={c} value={c}>{t(`support.category.${c}`)}</option>)}
                        </select>
                    </label>
                    <label className="block text-sm">{t("support.priority_label")}
                        <select data-testid="support-new-priority" value={priority} onChange={e => setPriority(e.target.value)}
                            className="surface-input mt-1 w-full px-3 py-2 text-sm">
                            {PRIORITIES.map(p => <option key={p} value={p}>{t(`support.priority.${p}`)}</option>)}
                        </select>
                    </label>
                </div>
                <label className="block text-sm">{t("support.message")}
                    <textarea data-testid="support-new-body" value={body} onChange={e => setBody(e.target.value)} rows={5} minLength={2} maxLength={8000}
                        className="surface-input mt-1 w-full px-3 py-2 text-sm resize-none" />
                </label>
                <div className="flex justify-end gap-2">
                    <button onClick={onClose} className="rounded-md px-3 py-2 text-sm" style={{ color: "var(--text-muted)" }}>{t("common.cancel")}</button>
                    <button data-testid="support-new-submit" onClick={submit} disabled={busy || subject.trim().length < 2 || body.trim().length < 2}
                        className="inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium disabled:opacity-60"
                        style={{ background: "var(--primary)", color: "var(--primary-fg)" }}>
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <MessageSquare size={14} />}{t("support.send")}
                    </button>
                </div>
            </div>
        </div>
    );
}
